/**
 * @file components/audit-tasks/preparation/ClientFullProfileCard.tsx
 * @description 客戶完整檔案內聯卡片。
 * 4 個 sub-tabs：土地 / 加工場 / 資質 / 歷史案件，全部 read-only。
 */

import { Card, Descriptions, Empty, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';

/** 土地檔案資料結構（對應 GET /api/clients/:id/lands 的 item） */
interface Land {
  id: number;
  parcelNo: string;
  address: string;
  areaSqm: number | null;
  ownership: string | null;
  cropOrUse: string | null;
}

/** 加工場資料結構（對應 GET /api/clients/:id/processing-sites 的 item） */
interface ProcessingSite {
  id: number;
  name: string;
  address: string;
  operations: unknown;
}

/** 客戶基本狀態欄位（對應 GET /api/clients/:id 的 item） */
interface ClientBasic {
  id: number;
  verificationStatus: string;
  verificationStatusReason: string | null;
  contractExpiresAt: string | null;
}

/** 歷史案件資料結構（對應 GET /api/clients/:id/cases 的 item） */
interface HistoricalCase {
  id: number;
  caseCode: string;
  scheme: string;
  verificationType: string;
  workflowState: string;
  closedAt: string | null;
}

interface Props {
  /** 客戶 ID（必填，所有子查詢都依賴此值作為 queryKey 參數） */
  clientId: number;
}

/**
 * 客戶完整檔案內聯卡片元件
 *
 * @param props.clientId 客戶 ID
 * @remarks
 *  - 同時並行請求 4 個後端 endpoint 取得不同維度資料
 *  - 全部唯讀展示；不在此元件中提供編輯入口
 *  - 載入中顯示「載入中…」、無資料顯示 antd `<Empty />`
 */
export function ClientFullProfileCard({ clientId }: Props) {
  /* 土地清單 */
  const lands = useQuery({
    queryKey: ['client-lands', clientId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ items: Land[] }>(`/api/clients/${clientId}/lands`, {
        scope: 'client-profile',
      });
      return res.data;
    },
  });

  /* 加工場清單 */
  const sites = useQuery({
    queryKey: ['client-processing-sites', clientId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ items: ProcessingSite[] }>(
        `/api/clients/${clientId}/processing-sites`,
        { scope: 'client-profile' },
      );
      return res.data;
    },
  });

  /* 客戶基本+資質資訊（單筆） */
  const basic = useQuery({
    queryKey: ['client-basic-for-profile', clientId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ item: ClientBasic }>(`/api/clients/${clientId}`, {
        scope: 'client-profile',
      });
      return res.data;
    },
  });

  /* 歷史案件清單 */
  const cases = useQuery({
    queryKey: ['client-cases', clientId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ items: HistoricalCase[] }>(
        `/api/clients/${clientId}/cases`,
        { scope: 'client-profile' },
      );
      return res.data;
    },
  });

  /* 土地表格欄位 */
  const landCols: ColumnsType<Land> = [
    {
      title: '地號',
      dataIndex: 'parcelNo',
      width: 140,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    { title: '地址', dataIndex: 'address', ellipsis: true },
    { title: '面積 (m²)', dataIndex: 'areaSqm', width: 120 },
    { title: '所有權', dataIndex: 'ownership', width: 100 },
    { title: '用途', dataIndex: 'cropOrUse', width: 140 },
  ];

  /* 加工場表格欄位 */
  const siteCols: ColumnsType<ProcessingSite> = [
    { title: '名稱', dataIndex: 'name', width: 200 },
    { title: '地址', dataIndex: 'address', ellipsis: true },
  ];

  /* 歷史案件表格欄位 */
  const caseCols: ColumnsType<HistoricalCase> = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      width: 180,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    { title: '方案', dataIndex: 'scheme', width: 100 },
    { title: '類型', dataIndex: 'verificationType', width: 120 },
    {
      title: '狀態',
      dataIndex: 'workflowState',
      width: 100,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: '結案時間',
      dataIndex: 'closedAt',
      width: 160,
      render: (d: string | null) => (d ? new Date(d).toLocaleDateString('zh-TW') : '—'),
    },
  ];

  return (
    <Card size="small" title="客戶完整檔案">
      <Tabs
        items={[
          {
            key: 'lands',
            label: `土地 (${lands.data?.items.length ?? 0})`,
            children: lands.isLoading ? (
              <Typography.Text>載入中…</Typography.Text>
            ) : lands.data?.items.length ? (
              <Table<Land>
                rowKey="id"
                dataSource={lands.data.items}
                columns={landCols}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty />
            ),
          },
          {
            key: 'sites',
            label: `加工場 (${sites.data?.items.length ?? 0})`,
            children: sites.isLoading ? (
              <Typography.Text>載入中…</Typography.Text>
            ) : sites.data?.items.length ? (
              <Table<ProcessingSite>
                rowKey="id"
                dataSource={sites.data.items}
                columns={siteCols}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty />
            ),
          },
          {
            key: 'quals',
            label: '驗證狀態',
            children: basic.isLoading ? (
              <Typography.Text>載入中…</Typography.Text>
            ) : basic.data?.item ? (
              <Descriptions size="small" column={2} bordered>
                {/* 驗證業務屬性（資格/類型/類別/合約來源等）已下放至案件，客戶層僅保留狀態類資訊 */}
                <Descriptions.Item label="驗證狀態">
                  {basic.data.item.verificationStatus ? (
                    <Tag>{basic.data.item.verificationStatus}</Tag>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="合約到期">
                  {basic.data.item.contractExpiresAt
                    ? new Date(basic.data.item.contractExpiresAt).toLocaleDateString('zh-TW')
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="狀態原因" span={2}>
                  {basic.data.item.verificationStatusReason || '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty />
            ),
          },
          {
            key: 'cases',
            label: `歷史案件 (${cases.data?.items.length ?? 0})`,
            children: cases.isLoading ? (
              <Typography.Text>載入中…</Typography.Text>
            ) : cases.data?.items.length ? (
              <Table<HistoricalCase>
                rowKey="id"
                dataSource={cases.data.items}
                columns={caseCols}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty />
            ),
          },
        ]}
      />
    </Card>
  );
}
